// =============================================
// Admin Categories — קטגוריות הוצאה
// Dual-mode: Network Manager (OwnerLevel=1) + SysAdmin (OwnerLevel=0)
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderTree, Plus, Search, Pencil, Trash2, X,
  Loader2, ArrowUpDown, Hash
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import { adminApi } from '../../services/api';
import type { ExpenseCategory } from '../../stores/teacherStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminCategoryHelpTexts } from '../../constants/helpTexts';
import { ModalWrapper } from '../../components/shared/ModalWrapper';
import { SuccessToast } from '../../components/shared/SuccessToast';
import { useSuccessToast } from '../../hooks/useSuccessToast';
import { adminColors } from '../../styles/colors';

const colors = {
  ...adminColors,
  sysAdmin: '#7c3aed',
  sysAdminLight: '#f5f3ff',
  green: '#22c55e',
  greenLight: '#dcfce7',
  red: '#ef4444',
  redLight: '#fef2f2',
};

// Common Lucide icon names for categories
const ICON_SUGGESTIONS = [
  'utensils-crossed', 'palette', 'sparkles', 'file-text', 'gamepad-2',
  'book-open', 'trees', 'wrench', 'party-popper', 'more-horizontal',
  'heart', 'music', 'camera', 'gift', 'star', 'flag', 'scissors',
  'truck', 'phone', 'printer', 'lamp', 'shirt', 'umbrella', 'key',
  'laptop', 'tablet', 'brush', 'paint-bucket', 'ruler', 'baby',
];

export default function AdminCategories() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();

  // Detect mode: SysAdmin (no org) vs Network Manager (has org)
  const isSysAdmin = !user?.organizationPublicId;
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;
  const themeColor = isSysAdmin ? colors.sysAdmin : colors.primary;
  const themeLight = isSysAdmin ? colors.sysAdminLight : colors.primaryLight;

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { message: successMessage, show: showSuccess } = useSuccessToast();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);
  const [formName, setFormName] = useState('');
  const [formIcon, setFormIcon] = useState('');
  const [formSortOrder, setFormSortOrder] = useState('0');
  const [saving, setSaving] = useState(false);
  const [showIconSuggestions, setShowIconSuggestions] = useState(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Load data — always use merged endpoint (system + org categories)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // getCategories calls /management/all-categories
        // Without orgFilter: returns system categories (OwnerLevel=0) only
        // With orgFilter: returns system + org categories
        const result = await adminApi.getCategories(orgFilter);
        if (result.success && result.data) {
          setCategories(result.data);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError('שגיאה בטעינת קטגוריות');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgFilter, isSysAdmin]);

  // Filter categories by search
  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.icon && c.icon.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Open modal for add
  const openAddModal = () => {
    setEditingCategory(null);
    setFormName('');
    setFormIcon('');
    setFormSortOrder(String(categories.length + 1));
    setShowIconSuggestions(false);
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (category: ExpenseCategory) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormIcon(category.icon || '');
    setFormSortOrder(String(category.sortOrder));
    setShowIconSuggestions(false);
    setShowModal(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: formName.trim(),
        icon: formIcon.trim() || undefined,
        sortOrder: parseInt(formSortOrder) || 0,
      };

      if (editingCategory) {
        // Update
        const result = isSysAdmin && !orgFilter
          ? await adminApi.updateSystemCategory(editingCategory.id, data)
          : await adminApi.updateCategory(editingCategory.id, data);
        if (result.success && result.data) {
          setCategories(prev => prev.map(c => c.id === editingCategory.id ? result.data : c));
          showSuccess('קטגוריה עודכנה בהצלחה');
        }
      } else {
        // Create
        const result = isSysAdmin && !orgFilter
          ? await adminApi.createSystemCategory(data)
          : await adminApi.createCategory(data, orgFilter);
        if (result.success && result.data) {
          setCategories(prev => [...prev, result.data]);
          showSuccess('קטגוריה נוצרה בהצלחה');
        }
      }
      setShowModal(false);
    } catch (err) {
      console.error('Failed to save category:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async (id: number) => {
    try {
      const result = isSysAdmin && !orgFilter
        ? await adminApi.deleteSystemCategory(id)
        : await adminApi.deleteCategory(id);
      if (result.success) {
        setCategories(prev => prev.filter(c => c.id !== id));
        showSuccess('קטגוריה נמחקה בהצלחה');
      }
    } catch (err) {
      console.error('Failed to delete category:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const ownerLevelLabel = (level: number) => {
    switch (level) {
      case 0: return 'מערכת';
      case 1: return 'רשת';
      case 2: return 'גן';
      default: return 'לא ידוע';
    }
  };

  const ownerLevelColor = (level: number) => {
    switch (level) {
      case 0: return { bg: colors.sysAdminLight, text: colors.sysAdmin };
      case 1: return { bg: colors.primaryLight, text: colors.primary };
      case 2: return { bg: colors.greenLight, text: colors.green };
      default: return { bg: colors.background, text: colors.textLight };
    }
  };

  return (
    <div style={{ direction: 'rtl', maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FolderTree size={28} color={themeColor} />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.text, margin: 0 }}>
              קטגוריות הוצאה
            </h1>
          </div>
          <p style={{ fontSize: 14, color: colors.textLight, marginTop: 4 }}>
            {isSysAdmin && !orgFilter
              ? 'קטגוריות ברמת מערכת — זמינות לכל הארגונים'
              : 'קטגוריות ברמת רשת — זמינות לגנים ברשת'}
            {' · '}{categories.length} קטגוריות
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={openAddModal}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 20px', borderRadius: 10,
            background: themeColor, color: '#fff',
            border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
          }}
        >
          <Plus size={18} />
          הוסף קטגוריה
        </motion.button>
      </div>

      {/* Success message */}
      <SuccessToast message={successMessage} />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-center">{error}</div>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Search size={18} color={colors.textLight} style={{ position: 'absolute', right: 12, top: 12 }} />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש קטגוריה..."
          style={{
            width: '100%', padding: '10px 40px 10px 12px',
            borderRadius: 10, border: `1px solid ${colors.border}`,
            fontSize: 14, outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textLight }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 12 }}>טוען קטגוריות...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textLight }}>
          <FolderTree size={48} color={colors.border} style={{ marginBottom: 12 }} />
          <h3 style={{ color: colors.text, marginBottom: 4 }}>
            {searchQuery ? 'לא נמצאו קטגוריות' : 'אין קטגוריות'}
          </h3>
          <p style={{ fontSize: 14 }}>
            {searchQuery ? 'נסה חיפוש אחר' : 'הוסף קטגוריות לקטלוג כדי שגננות יוכלו להשתמש בהן'}
          </p>
        </div>
      ) : (
        /* Category list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredCategories.map((category) => (
            <motion.div
              key={category.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px', borderRadius: 12,
                background: colors.white, border: `1px solid ${colors.border}`,
                transition: 'box-shadow 0.2s',
              }}
              whileHover={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Icon display */}
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: themeLight, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, color: themeColor, fontWeight: 600,
                  fontFamily: 'monospace',
                }}>
                  {category.icon ? category.icon.substring(0, 3) : '—'}
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 15, color: colors.text }}>
                      {category.name}
                    </span>
                    {/* Owner level badge */}
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 6,
                      background: ownerLevelColor(category.ownerLevel).bg,
                      color: ownerLevelColor(category.ownerLevel).text,
                      fontWeight: 600,
                    }}>
                      {ownerLevelLabel(category.ownerLevel)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 3 }}>
                    {category.icon && (
                      <span style={{ fontSize: 12, color: colors.textLight, fontFamily: 'monospace' }}>
                        {category.icon}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: colors.textLight, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <ArrowUpDown size={11} />
                      סדר: {category.sortOrder}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {deletingId === category.id ? (
                  <>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(category.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 8,
                        background: colors.red, color: '#fff',
                        border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600,
                      }}
                    >
                      מחק
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setDeletingId(null)}
                      style={{
                        padding: '6px 14px', borderRadius: 8,
                        background: colors.background, color: colors.text,
                        border: `1px solid ${colors.border}`, cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      ביטול
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.button
                      whileHover={{ background: themeLight }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => openEditModal(category)}
                      style={{
                        padding: 8, borderRadius: 8,
                        background: 'transparent', border: 'none',
                        cursor: 'pointer', color: themeColor,
                        display: 'flex',
                      }}
                    >
                      <Pencil size={16} />
                    </motion.button>
                    <motion.button
                      whileHover={{ background: colors.redLight }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setDeletingId(category.id)}
                      style={{
                        padding: 8, borderRadius: 8,
                        background: 'transparent', border: 'none',
                        cursor: 'pointer', color: colors.red,
                        display: 'flex',
                      }}
                    >
                      <Trash2 size={16} />
                    </motion.button>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <ModalWrapper isOpen={showModal} onClose={() => setShowModal(false)} maxWidth="460px">
        <>
              {/* Modal header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: colors.text, margin: 0 }}>
                  {editingCategory ? 'עריכת קטגוריה' : 'הוספת קטגוריה'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textLight, padding: 4 }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Name field */}
              <div style={{ marginBottom: 16 }}>
                <FormLabel text="שם קטגוריה" required helpText={adminCategoryHelpTexts.name.tooltip} style={{ color: colors.text }} />
                <input
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="לדוגמה: ציוד חד פעמי"
                  style={{
                    width: '100%', padding: '10px 14px',
                    borderRadius: 10, border: `1px solid ${colors.border}`,
                    fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  }}
                  autoFocus
                />
              </div>

              {/* Icon field */}
              <div style={{ marginBottom: 16 }}>
                <FormLabel text="אייקון (שם Lucide)" helpText={adminCategoryHelpTexts.icon.tooltip} style={{ color: colors.text }} />
                <input
                  value={formIcon}
                  onChange={e => setFormIcon(e.target.value)}
                  onFocus={() => setShowIconSuggestions(true)}
                  placeholder="לדוגמה: utensils-crossed"
                  style={{
                    width: '100%', padding: '10px 14px',
                    borderRadius: 10, border: `1px solid ${colors.border}`,
                    fontSize: 14, outline: 'none', boxSizing: 'border-box',
                    fontFamily: 'monospace',
                  }}
                />
                <HelperText text={adminCategoryHelpTexts.icon.helper!} />
                {/* Icon suggestions */}
                <AnimatePresence>
                  {showIconSuggestions && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8,
                        padding: 8, borderRadius: 8, background: colors.background,
                        maxHeight: 100, overflowY: 'auto',
                      }}>
                        {ICON_SUGGESTIONS.filter(i =>
                          !formIcon || i.includes(formIcon.toLowerCase())
                        ).map(icon => (
                          <button
                            key={icon}
                            onClick={() => { setFormIcon(icon); setShowIconSuggestions(false); }}
                            style={{
                              padding: '3px 8px', borderRadius: 6,
                              background: formIcon === icon ? themeColor : colors.white,
                              color: formIcon === icon ? '#fff' : colors.text,
                              border: `1px solid ${colors.border}`,
                              cursor: 'pointer', fontSize: 11, fontFamily: 'monospace',
                            }}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Sort order field */}
              <div style={{ marginBottom: 24 }}>
                <FormLabel text="סדר מיון" helpText={adminCategoryHelpTexts.sortOrder.tooltip} style={{ color: colors.text }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Hash size={16} color={colors.textLight} />
                  <input
                    type="number"
                    value={formSortOrder}
                    onChange={e => setFormSortOrder(e.target.value)}
                    min="0"
                    style={{
                      width: 100, padding: '10px 14px',
                      borderRadius: 10, border: `1px solid ${colors.border}`,
                      fontSize: 14, outline: 'none', textAlign: 'center',
                    }}
                  />
                </div>
                <HelperText text={adminCategoryHelpTexts.sortOrder.helper!} />
              </div>

              {/* Submit button */}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSave}
                disabled={!formName.trim() || saving}
                style={{
                  width: '100%', padding: '12px 0',
                  borderRadius: 10, border: 'none',
                  background: !formName.trim() ? colors.border : themeColor,
                  color: '#fff', fontSize: 15, fontWeight: 600,
                  cursor: !formName.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                {saving ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  editingCategory ? 'עדכון' : 'הוספה'
                )}
              </motion.button>
        </>
      </ModalWrapper>
    </div>
  );
}
